import { type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { NewChatIcon, PlusIcon, SendIcon, StopIcon } from "../../shared/components/icons";
import type { SlashCommand, SlashContext } from "../slashCommands";
import { SlashCommandPalette } from "./SlashCommandPalette";
import styles from "./ChatInputBar.module.css";
import { UPLOAD_ACCEPT_ATTRIBUTE, isSupportedUpload, unsupportedUploadMessage } from "../attachmentSupport";
import {
  FILE_TOO_LARGE_MESSAGE,
  MAX_FILE_BYTES,
  MAX_SESSION_FILES,
  SESSION_BUDGET_MESSAGE,
  SESSION_RAW_BYTE_BUDGET,
  TOO_MANY_FILES_MESSAGE,
} from "../uploadLimits";

interface ChatInputBarProps {
  readonly input: string;
  readonly onInputChange: (value: string) => void;
  readonly onSubmit: (e: FormEvent, attachedContent?: string, attachedFileName?: string) => void;
  readonly disabled: boolean;
  readonly streaming?: boolean;
  readonly onStop?: () => void;
  readonly placeholder?: string;
  readonly commands?: readonly SlashCommand[];
  readonly onSlashCommand?: (cmd: SlashCommand) => void;
  /** Channel state consulted by ``autoSendWhen`` predicates when a palette
   *  item is picked. When omitted, every command falls back to prefill. */
  readonly slashContext?: SlashContext;
  /** Previously sent messages, newest first. Up/Down arrows navigate this list. */
  readonly history?: readonly string[];
  readonly onNewChat?: () => void;
  readonly attachedFiles?: File[];
  readonly onAttachFiles?: (files: File[]) => void;
  readonly onRemoveFile?: (index: number) => void;
  /** Files already retained for the whole session (across turns); used to cap total uploads. */
  readonly sessionFileCount?: number;
  /** Raw bytes of the files already retained for the session — the cumulative
   *  budget check needs sizes, not just a count (AIVA-7072). */
  readonly sessionFileBytes?: number;
  /** Persistent context warning (e.g. "selection larger than the chat cap").
   *  State-driven: shows for exactly as long as the condition holds, and a
   *  transient upload error temporarily takes its place rather than stacking. */
  readonly contextNotice?: string | null;
}

const TEXTAREA_MIN_HEIGHT = 22;
const TEXTAREA_MAX_HEIGHT = 160;

// Upload guards live in ../uploadLimits — one module owns every limit AND its
// rejection copy, so the numbers users see can never drift from the numbers
// that are enforced (AIVA-7072).

export function ChatInputBar({
  input,
  onInputChange,
  onSubmit,
  disabled,
  streaming = false,
  onStop,
  placeholder,
  commands = [],
  onSlashCommand,
  slashContext,
  history = [],
  onNewChat,
  attachedFiles = [],
  onAttachFiles,
  onRemoveFile,
  sessionFileCount = 0,
  sessionFileBytes = 0,
  contextNotice = null,
}: Readonly<ChatInputBarProps>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // AIVA-7072: one dismiss timer, replaced on every new validation error.
  // Without clearing, a timer armed by an earlier failed pick fires mid-display
  // of the NEXT error and wipes it early — so the message held for anywhere
  // between an instant and 4s depending on upload rhythm, which QA read as
  // "inconsistent validation messages across multiple uploads".
  const uploadErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (uploadErrorTimerRef.current) clearTimeout(uploadErrorTimerRef.current);
    },
    [],
  );
  const hasText = input.trim().length > 0;
  const canSend = hasText || attachedFiles.length > 0;
  const [activeIndex, setActiveIndex] = useState(0);
  // True once the user dismisses the palette (Escape or click-outside) without
  // changing the input. Reset whenever the input changes so a fresh slash query
  // re-opens the palette.
  const [paletteDismissed, setPaletteDismissed] = useState(false);
  // -1 = not in history mode; ≥0 = index into `history` currently shown
  const historyIndexRef = useRef(-1);

  const showPalette =
    commands.length > 0 &&
    !!onSlashCommand &&
    input.startsWith("/") &&
    !input.includes(" ") &&
    !paletteDismissed;

  const paletteCommands = useMemo<readonly SlashCommand[]>(() => {
    if (!showPalette) return [];
    // The input is guaranteed to start with "/" and contain no whitespace
    // here (see showPalette). Command slashes include the leading "/", so
    // prefix-match the raw input; a bare "/" shows the full list.
    const prefix = input.toLowerCase();
    if (prefix === "/") return commands;
    return commands.filter((c) => c.slash.startsWith(prefix));
  }, [showPalette, commands, input]);

  useEffect(() => {
    setActiveIndex(0);
  }, [paletteCommands.length]);

  // Close the palette when the user clicks anywhere outside the input bar.
  // The textarea keeps its text — only the suggestion box is dismissed.
  useEffect(() => {
    if (!showPalette) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setPaletteDismissed(true);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showPalette]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_HEIGHT), TEXTAREA_MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, [input]);

  const handleFormSubmit = (e: FormEvent) => {
    onSubmit(e);
  };

  const selectCommand = (cmd: SlashCommand) => {
    // A command auto-sends when its registry ``autoSendWhen`` predicate says
    // so for the current channel state (e.g. /catchup always, Word /rewrite
    // only with a selection). Slashes without a predicate — and clicks made
    // before a slashContext is available — fall back to prefill.
    const autoSend = (slashContext && cmd.autoSendWhen?.(slashContext)) ?? false;
    if (autoSend) {
      // Auto-send slashes need no user args — clear the input and fire the
      // command immediately so the user gets a one-click "do it now"
      // experience.
      onInputChange("");
      onSlashCommand?.(cmd);
      return;
    }
    // Default: prefill the input with the slash sigil + space and
    // refocus the textarea so the user can type args (e.g. a sender
    // or subject) before pressing Enter. Manual typing of the same
    // shape is parsed by ``resolveSlashCommand`` in ``useChat``.
    onInputChange(`${cmd.slash} `);
    textareaRef.current?.focus();
  };

  const handlePaletteKey = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!showPalette) return false;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, paletteCommands.length - 1));
      return true;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return true;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const cmd = paletteCommands[activeIndex];
      if (cmd) selectCommand(cmd);
      return true;
    }
    if (e.key === "Escape") {
      // Just close the suggestion box — keep the command the user has typed.
      e.preventDefault();
      setPaletteDismissed(true);
      return true;
    }
    return false;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (handlePaletteKey(e)) return;
    if (e.key === "ArrowUp" && !showPalette && history.length > 0) {
      const cursorAtStart = e.currentTarget.selectionStart === 0;
      if (input === "" || cursorAtStart) {
        e.preventDefault();
        const nextIndex = Math.min(historyIndexRef.current + 1, history.length - 1);
        historyIndexRef.current = nextIndex;
        onInputChange(history[nextIndex] ?? "");
        return;
      }
    }
    if (e.key === "ArrowDown" && !showPalette && historyIndexRef.current >= 0) {
      e.preventDefault();
      const nextIndex = historyIndexRef.current - 1;
      historyIndexRef.current = nextIndex;
      onInputChange(nextIndex >= 0 ? (history[nextIndex] ?? "") : "");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (hasText && !disabled) {
        const form = e.currentTarget.form;
        form?.requestSubmit();
      }
    }
  };

  return (
    <form ref={formRef} className={styles.inputBar} onSubmit={handleFormSubmit}>
      {showPalette && (
        <SlashCommandPalette
          commands={paletteCommands}
          activeIndex={activeIndex}
          onSelect={selectCommand}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        className={styles.fileInputHidden}
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          const all = Array.from(e.target.files ?? []);
          // Reset the input up front so re-picking the same file still fires change.
          e.target.value = "";
          if (all.length === 0) return;

          const errors: string[] = [];

          // 1. Type: whatever the backend's extraction pipeline can read. The
          // list lives in attachmentSupport.ts, mirrored from the Python module
          // and held there by a drift test — not restated here (AIVA-6901).
          const typeOk = all.filter((f) => isSupportedUpload(f.name));
          if (typeOk.length < all.length) {
            errors.push(unsupportedUploadMessage(all.filter((f) => !isSupportedUpload(f.name)).map((f) => f.name)));
          }

          // 2. Size: reject oversized files so one document can't bloat every turn.
          const sizeOk = typeOk.filter((f) => f.size <= MAX_FILE_BYTES);
          if (sizeOk.length < typeOk.length) {
            errors.push(FILE_TOO_LARGE_MESSAGE);
          }

          // 3. Count: cap how many distinct files accumulate across the session
          // (already-retained + currently pending + newly picked).
          const slots = Math.max(0, MAX_SESSION_FILES - sessionFileCount - attachedFiles.length);
          const countOk = sizeOk.slice(0, slots);
          if (countOk.length < sizeOk.length) {
            errors.push(TOO_MANY_FILES_MESSAGE);
          }

          // 4. Cumulative budget: the whole retained set rides EVERY turn
          // base64-encoded, and the gateway 413s requests over its cap. Reject
          // the overflow HERE, at attach time, so a file that attaches can
          // always be sent — previously a passing file was accepted and then
          // bounced later by the server with different copy (AIVA-7072).
          let usedBytes = sessionFileBytes + attachedFiles.reduce((sum, f) => sum + f.size, 0);
          const accepted: File[] = [];
          let overBudget = false;
          for (const f of countOk) {
            if (usedBytes + f.size <= SESSION_RAW_BYTE_BUDGET) {
              usedBytes += f.size;
              accepted.push(f);
            } else {
              overBudget = true;
            }
          }
          if (overBudget) {
            errors.push(SESSION_BUDGET_MESSAGE);
          }

          if (errors.length > 0) {
            const message = errors.join(" ");
            if (uploadErrorTimerRef.current) clearTimeout(uploadErrorTimerRef.current);
            setUploadError(message);
            // Hold the message long enough to actually read it: ~60 ms per
            // character plus a reaction buffer, clamped to 4–10 s. A short
            // per-file error keeps the familiar 4 s; the longer budget message
            // (or several errors joined) earns a longer window.
            const dismissMs = Math.min(10_000, Math.max(4_000, 1_500 + message.length * 60));
            uploadErrorTimerRef.current = setTimeout(() => {
              uploadErrorTimerRef.current = null;
              setUploadError(null);
            }, dismissMs);
          }
          if (accepted.length > 0) onAttachFiles?.(accepted);
        }}
      />
      {/* role="alert" per the spec's error precedent (Outlook §9.1: the
          sign-in error is role="alert") — announced immediately, which also
          softens the auto-dismiss for screen-reader users. */}
      {uploadError && (
        <p role="alert" className={styles.fileTypeError}>
          {uploadError}
        </p>
      )}
      {/* Persistent context warning, in the same slot as the upload error but
          amber: it describes a live condition (oversized selection), not a
          failed action. A transient upload error takes the slot while it
          shows; this returns when it dismisses if the condition still holds. */}
      {!uploadError && contextNotice && (
        <p role="status" className={styles.contextWarning}>
          {contextNotice}
        </p>
      )}
      <div className={`${styles.inputShell} ${hasText ? styles.inputShellHasText : ""}`}>
        {attachedFiles.length > 0 && (
          <div className={styles.attachmentRow}>
            {attachedFiles.map((file, i) => (
              <span key={`${file.name}-${i}`} className={styles.attachmentChip}>
                <span className={styles.attachmentName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.attachmentRemove}
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemoveFile?.(i)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className={styles.inputField}
          placeholder={placeholder ?? "Ask AiVA…"}
          value={input}
          onChange={(e) => {
            historyIndexRef.current = -1;
            setPaletteDismissed(false);
            onInputChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
        />
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <button
              type="button"
              className={styles.iconBtn}
              title="Start a new chat"
              aria-label="Start a new chat"
              onClick={onNewChat}
            >
              <NewChatIcon />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              title="Attach a file"
              aria-label="Attach a file"
              onClick={() => fileInputRef.current?.click()}
            >
              <PlusIcon />
            </button>
          </div>
          <div className={styles.toolbarRight}>
            {streaming && (
              <button
                className={styles.stopBtn}
                type="button"
                onClick={onStop}
                aria-label="Stop generating"
                title="Stop generating"
              >
                <StopIcon />
              </button>
            )}
            <button
              className={styles.sendBtn}
              type="submit"
              disabled={!canSend || disabled}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
